import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  ChevronUp,
  ChevronDown,
  CheckCircle,
  Award,
  Code2,
  X,
  Check,
  Edit3
} from 'lucide-react';
import QuillEditorWithImageResize from '../QuillEditorWithImageResize';
import CodeEditorWithMonaco from './CodeEditorWithMonaco';

interface Option {
  id: string;
  label: string;
  text: string;
  isCorrect: boolean;
  image?: string;
  imageWidth?: number;
  images?: string[];
  imageWidths?: number[];
}

interface Question {
  id: string;
  type: string;
  questionTitle?: string; // 题目名称（管理员手动输入的短名称）
  title: string;
  score: number;
  content?: string;
  referenceAnswer?: string;
  scoringPoints?: string;
  // QuestionRenderer 需要的字段
  options?: Option[];
  titleImages?: string[];
  titleImageWidths?: number[];
  titleImageLayout?: string;
  imagePosition?: string;
  optionLayout?: string;
  optionImageWidths?: Record<string, number>;
  explanation?: string;
  answerText?: string;
  codeTemplate?: string;
  codeLanguage?: string;
  allowImageUpload?: boolean;
  maxImageSize?: number;
  allowTextInput?: boolean;
  maxTextLength?: number;
  allowFileUpload?: boolean;
  maxFileSize?: number;
  answerImages?: string[];
  answerImageWidths?: number[];
  scoringPointsImages?: string[];
  scoringPointsImageWidths?: number[];
  referenceAnswerImages?: string[];
  referenceAnswerImageWidths?: number[];
  [key: string]: any;
}

interface PythonQuestionEditorProps {
  question: Question;
  paperId?: string;
  isPreviewMode?: boolean;
  currentIndex?: number;
  examQuestions?: Question[];
  totalCount?: number;
  // 标题和快捷操作回调
  onTitleChange?: (title: string) => void;
  onScoreChange?: (score: number) => void;
  onCopyQuestion?: () => void;
  onDeleteQuestion?: () => void;
  onInsertQuestion?: () => void;
  // 导航回调
  onPrevQuestion?: () => void;
  onNextQuestion?: () => void;
  // 编辑回调
  onContentChange?: (content: string) => void;
  onReferenceAnswerChange?: (referenceAnswer: string) => void;
  onScoringPointsChange?: (scoringPoints: string) => void;
  // 编辑模式相关
  isEditing?: boolean;
  onEditQuestion?: () => void;
  onExitEdit?: () => void;
  onSaveQuestion?: (question: Question) => Promise<void>;
  onCancelEdit?: () => void;
}

export default function PythonQuestionEditor({
  question,
  paperId,
  isPreviewMode = false,
  currentIndex = 0,
  examQuestions = [],
  totalCount,
  onTitleChange,
  onScoreChange,
  onCopyQuestion,
  onDeleteQuestion,
  onInsertQuestion,
  onPrevQuestion,
  onNextQuestion,
  onContentChange,
  onReferenceAnswerChange,
  onScoringPointsChange,
  isEditing = false,
  onEditQuestion,
  onExitEdit,
  onSaveQuestion,
  onCancelEdit,
}: PythonQuestionEditorProps) {
  // 防止组件卸载后执行异步操作
  const unmountedRef = useRef(false);
  useEffect(() => {
    unmountedRef.current = false;
    return () => { unmountedRef.current = true; };
  }, []);

  // 编辑草稿状态
  const [editDraft, setEditDraft] = useState<Question>(question);

  // 当 question.id 变化时重置草稿
  useEffect(() => {
    if (question && question.id) {
      requestAnimationFrame(() => {
        setEditDraft(question);
      });
    }
  }, [question?.id]);

  // 当进入编辑模式时，用最新的 question 重置草稿
  // 确保编辑器能正确加载已保存的图片宽度等数据
  useEffect(() => {
    if (isEditing) {
      setEditDraft(question);
    }
  }, [isEditing]);

  // 更新草稿的辅助函数
  const updateDraft = (updates: Partial<Question>) => {
    setEditDraft(prev => ({ ...prev, ...updates }));
  };

  // 保存处理
  const handleSave = async () => {
    if (unmountedRef.current) return;
    try {
      await onSaveQuestion?.(editDraft);
      if (!unmountedRef.current) {
        onExitEdit?.();
      }
    } catch (error) {
      if (unmountedRef.current) return;
      console.error('保存题目失败:', error);
      alert('保存失败，请重试');
    }
  };

  // 取消处理
  const handleCancel = () => {
    setEditDraft(question);
    onExitEdit?.();
  };

  // 题目名称独立管理，从 question.questionTitle（管理员输入的名称）初始化
  const [titleInputValue, setTitleInputValue] = useState(question.questionTitle ?? question.title ?? '');
  const [scoreInputValue, setScoreInputValue] = useState(question.score || 0);

  // 同步题目名称和分值
  useEffect(() => {
    setTitleInputValue(question.questionTitle ?? question.title ?? '');
  }, [question.questionTitle, question.title]);

  useEffect(() => {
    setScoreInputValue(question.score || 0);
  }, [question.score]);

  // 处理标题变更
  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTitle = e.target.value;
    setTitleInputValue(newTitle);
    if (isEditing) {
      updateDraft({ title: newTitle, questionTitle: newTitle });
    }
    onTitleChange?.(newTitle);
  };

  // 处理分值变更
  const handleScoreChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newScore = parseInt(e.target.value) || 0;
    setScoreInputValue(newScore);
    if (isEditing) {
      updateDraft({ score: newScore });
    }
    onScoreChange?.(newScore);
  };

  // 折叠状态管理
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    stem: true,
    answer: true,
    reference: !isPreviewMode,
    rules: !isPreviewMode,
  });

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // 获取题型标签
  const getQuestionTypeLabel = () => {
    if (question.type === 'python_coding') {
      return 'Python编程题';
    }
    return 'Python编程题';
  };

  // 渲染作答配置区（仅编辑模式显示）
  const renderAnswerConfigArea = () => {
    return (
      <div className="space-y-3">
        {/* 固定提交规则说明 */}
        <div className="flex items-center gap-2 px-4 py-3 bg-blue-50 rounded-lg border border-blue-100">
          <Code2 className="w-4 h-4 text-blue-600 shrink-0" />
          <span className="text-sm text-blue-700">
            学生需在代码编辑器中编写 Python 代码，系统将自动执行并判分
          </span>
        </div>

        {/* 代码模板输入框 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            代码模板（可选）
          </label>
          <CodeEditorWithMonaco
            value={editDraft.codeTemplate || ''}
            onChange={(value) => updateDraft({ codeTemplate: value })}
            language="python"
            height="200px"
            placeholder="# 请在此处编写Python代码模板\n# 学生作答时将基于此模板进行编辑"
          />
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* 1. 顶部导航栏 - 编辑模式显示保存/取消按钮，预览模式显示题型标签 */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0 }}
        className="bg-white rounded-xl border border-gray-200 overflow-hidden"
      >
        <div className="flex items-center justify-between p-3">
          {/* 左侧：题型标签 */}
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center px-4 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg whitespace-nowrap shadow-sm">
              {getQuestionTypeLabel()}
            </span>
          </div>

          {/* 右侧：编辑模式显示保存/取消按钮，预览模式显示编辑按钮 */}
          <div className="flex items-center gap-2 shrink-0">
            {isEditing ? (
              <>
                <button
                  onClick={handleCancel}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
                >
                  <X className="w-4 h-4" />
                  取消
                </button>
                <button
                  onClick={handleSave}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500 text-white text-sm font-medium rounded-lg hover:bg-emerald-600 transition-colors shadow-sm"
                >
                  <Check className="w-4 h-4" />
                  完成
                </button>
              </>
            ) : (
              onEditQuestion && (
                <button
                  onClick={onEditQuestion}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-500 text-white text-sm font-medium rounded-lg hover:bg-blue-600 transition-colors shadow-sm"
                >
                  <Edit3 className="w-4 h-4" />
                  编辑题目
                </button>
              )
            )}
          </div>
        </div>
      </motion.div>

      {/* 预览模式：显示只读内容 */}
      {!isEditing && (
        <div className="space-y-4">
          {/* 题干区域 */}
          {question.content && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl border border-gray-200 overflow-hidden"
            >
              <div className="p-4">
                <h3 className="text-sm font-medium text-gray-700 mb-3">题目内容</h3>
                <div
                  className="prose max-w-none"
                  dangerouslySetInnerHTML={{ __html: question.content }}
                />
              </div>
            </motion.div>
          )}

          {/* 参考答案区域 */}
          {question.referenceAnswer && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl border border-gray-200 overflow-hidden"
            >
              <div className="p-4">
                <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                  <Award className="w-4 h-4 text-purple-600" />
                  参考答案
                </h3>
                <div
                  className="prose max-w-none"
                  dangerouslySetInnerHTML={{ __html: question.referenceAnswer }}
                />
              </div>
            </motion.div>
          )}

          {/* 评分要点区域 */}
          {question.scoringPoints && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl border border-gray-200 overflow-hidden"
            >
              <div className="p-4">
                <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  评分要点
                </h3>
                <div
                  className="prose max-w-none"
                  dangerouslySetInnerHTML={{ __html: question.scoringPoints }}
                />
              </div>
            </motion.div>
          )}
        </div>
      )}

      {/* 编辑模式：显示编辑区域 */}
      {isEditing && expandedSections.stem && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="bg-white rounded-xl border border-gray-200 overflow-hidden"
        >
          <div className="p-4 space-y-4">
            {/* 题目名称输入框 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                题目名称
              </label>
              <input
                type="text"
                value={titleInputValue}
                onChange={handleTitleChange}
                placeholder="请输入题目名称（可选）"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none transition-all"
              />
            </div>

            {/* 富文本编辑器 */}
            <QuillEditorWithImageResize
              value={editDraft.content || ''}
              onChange={(val: string, widths?: number[]) => {
                updateDraft({ content: val });
                if (widths && widths.length > 0) {
                  updateDraft({ titleImageWidths: widths });
                }
              }}
              questionId={question.id}
              imageWidths={editDraft.titleImageWidths || []}
              onImageWidthsChange={(widths) => updateDraft({ titleImageWidths: widths })}
              applyRealtimeScaling={true}
              placeholder="请输入题目内容，支持图文混排..."
            />
          </div>
        </motion.div>
      )}

      {/* 作答配置区 - 仅编辑模式显示 */}
      {isEditing && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl border border-gray-200 overflow-hidden"
        >
          <button
            onClick={() => toggleSection('answer')}
            className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
          >
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="font-medium text-gray-900">作答配置</span>
            </div>
            {expandedSections.answer ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>
          {expandedSections.answer && (
            <div className="p-4">
              {renderAnswerConfigArea()}
            </div>
          )}
        </motion.div>
      )}

      {/* 参考答案区 - 仅编辑模式显示 */}
      {isEditing && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl border border-gray-200 overflow-hidden"
        >
          <button
            onClick={() => toggleSection('reference')}
            className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Award className="w-5 h-5 text-purple-600" />
              <span className="font-medium text-gray-900">参考答案</span>
            </div>
            {expandedSections.reference ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>
          {expandedSections.reference && (
            <div className="p-4">
              <QuillEditorWithImageResize
                value={editDraft.referenceAnswer || ''}
                onChange={(val: string, widths?: number[]) => {
                  updateDraft({ referenceAnswer: val });
                  if (widths && widths.length > 0) {
                    updateDraft({ referenceAnswerImageWidths: widths });
                  }
                }}
                questionId={question.id}
                imageWidths={editDraft.referenceAnswerImageWidths || []}
                onImageWidthsChange={(widths) => updateDraft({ referenceAnswerImageWidths: widths })}
                applyRealtimeScaling={true}
                placeholder="请输入参考答案..."
              />
            </div>
          )}
        </motion.div>
      )}

      {/* 评分要点区 - 仅编辑模式显示 */}
      {isEditing && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl border border-gray-200 overflow-hidden"
        >
          <button
            onClick={() => toggleSection('rules')}
            className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Award className="w-5 h-5 text-amber-600" />
              <span className="font-medium text-gray-900">评分要点</span>
            </div>
            {expandedSections.rules ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>
          {expandedSections.rules && (
            <div className="p-4">
              <QuillEditorWithImageResize
                value={editDraft.scoringPoints || ''}
                onChange={(val: string, widths?: number[]) => {
                  updateDraft({ scoringPoints: val });
                  if (widths && widths.length > 0) {
                    updateDraft({ scoringPointsImageWidths: widths });
                  }
                }}
                questionId={question.id}
                imageWidths={editDraft.scoringPointsImageWidths || []}
                onImageWidthsChange={(widths) => updateDraft({ scoringPointsImageWidths: widths })}
                applyRealtimeScaling={true}
                placeholder="请输入评分要点..."
              />
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
}
